import type { MembraneClient } from "@membranehq/sdk";
import { computed, ref, watch, type Ref, type WatchStopHandle } from "vue";

import type {
  Controller,
  GraphDatabaseDictionaryDictionary,
  IntegrationAppEnabledDataCollection,
  IntegrationAppEnabledDataCollectionId,
  IntegrationAppWorkspaceConfiguration,
  ReleasableRef,
  TaskBindingDictionary,
} from "@ctrl/controller-rpc";

import type { BaseLogger } from "@/components/inject";
import type {
  OrganizationIntegrationsView,
  UseIntegrations,
} from "@/components/integrations/useIntegrations";
import { integrationSyncConfigs } from "@/components/settings/integrations/integrationSyncConfigRegistry";
import type { IntegrationSyncConfig } from "@/components/settings/integrations/useIntegrationSyncWorkflow";
import type { UseProfile, Workspace } from "@/composables/useProfile";
import { releaseBestEffort } from "@/ref";
import { isRecord, optionalStringProperty } from "@/utils/unknown";

import { ensureDataCollectionBinding } from "./useDataCollectionBinding";
import type { WorkspaceDataItem } from "./useMembraneSetup";

export type TaskBindingRepairCandidate = {
  dataCollectionId: IntegrationAppEnabledDataCollectionId;
  dataCollection: Extract<
    IntegrationAppEnabledDataCollection,
    { status: "ready" }
  >;
  config: IntegrationSyncConfig;
};

type RepairConnection = {
  id: string;
  integrationId?: string;
};

type RepairIntegration = {
  key?: string;
};

/**
 * Finds ready integration collections whose backing table has no task binding.
 *
 * Kept pure so startup repair and later reactive updates use exactly the same
 * selection policy.
 */
export function missingTaskBindingCandidates(
  workspaceId: string,
  data: IntegrationAppWorkspaceConfiguration | null | undefined,
  bindings: TaskBindingDictionary,
  connections: readonly RepairConnection[],
  getServiceById: (integrationId: string) => RepairIntegration | undefined
): TaskBindingRepairCandidate[] {
  const connectionById = new Map(
    connections.map((connection) => [connection.id, connection])
  );
  const boundTables = new Set(
    Object.values(bindings)
      .filter((binding) => binding.workspace_id === workspaceId)
      .map((binding) => binding.table_id)
  );
  const claimedTables = new Set<string>();

  return (data?.enabled_data_collections ?? []).flatMap(
    ([dataCollectionId, dataCollection]) => {
      if (dataCollection.status !== "ready") return [];
      if (
        boundTables.has(dataCollection.table) ||
        claimedTables.has(dataCollection.table)
      ) {
        return [];
      }

      const connection = connectionById.get(dataCollectionId.connection_id);
      const integration = connection?.integrationId
        ? getServiceById(connection.integrationId)
        : undefined;
      const integrationKey = integration
        ? optionalStringProperty(integration, "key")
        : undefined;
      const config = integrationKey
        ? integrationSyncConfigs[integrationKey]
        : undefined;
      if (
        !config ||
        dataCollectionId.data_collection_key !== config.collectionKey
      ) {
        return [];
      }

      claimedTables.add(dataCollection.table);
      return [{ dataCollectionId, dataCollection, config }];
    }
  );
}

type OwnedWorkspace = {
  workspace: WorkspaceDataItem;
  stop: WatchStopHandle;
};

type TaskBindingRepairIntegrations = Pick<
  UseIntegrations,
  "client" | "forOrganization"
>;

/**
 * App-lifetime repair for integration task bindings.
 *
 * A collection may become ready after its setup route was closed, before that
 * route installed its watcher, or after recovering from an error. Subscribing
 * once per workspace here makes all three cases converge without requiring the
 * user to revisit Settings.
 */
export async function useIntegrationTaskBindingRepair(
  controller: Controller,
  baseLogger: BaseLogger,
  profile: Ref<UseProfile>,
  integrations: TaskBindingRepairIntegrations,
  workspaceDatabases: Ref<GraphDatabaseDictionaryDictionary | undefined>
): Promise<{ release: () => void }> {
  const logger = baseLogger.forModule("IntegrationTaskBindingRepair");
  const bindings = await controller.task.binding.all();
  const owned = new Map<string, OwnedWorkspace>();
  const acquiring = new Set<string>();
  const pending = new Set<string>();
  let released = false;

  const releaseWorkspace = (workspaceId: string) => {
    const entry = owned.get(workspaceId);
    if (!entry) return;
    owned.delete(workspaceId);
    entry.stop();
    releaseBestEffort(entry.workspace.membraneData);
  };

  const repairWorkspace = async (
    workspace: WorkspaceDataItem,
    view: OrganizationIntegrationsView,
    membrane: MembraneClient
  ) => {
    const candidates = missingTaskBindingCandidates(
      workspace.workspaceId,
      workspace.membraneData.value,
      bindings.value ?? {},
      view.connections.value,
      view.getServiceById
    );

    for (const candidate of candidates) {
      const tableId = candidate.dataCollection.table;
      const fields =
        workspaceDatabases.value?.[workspace.workspaceId]?.[tableId]?.type
          ?.fields;
      // Database propagation follows collection readiness. The reactive scan
      // runs again when it arrives; creating the binding before then would
      // leave its mappings permanently incomplete.
      if (!fields || Object.keys(fields).length === 0) continue;

      const key = `${workspace.workspaceId}:${tableId}`;
      if (pending.has(key)) continue;
      pending.add(key);
      try {
        const parameters = isRecord(candidate.dataCollectionId.parameters)
          ? candidate.dataCollectionId.parameters
          : {};
        await ensureDataCollectionBinding(
          workspace,
          controller,
          workspaceDatabases,
          candidate.dataCollectionId,
          candidate.dataCollection,
          candidate.config.fieldsFor?.(parameters) ?? candidate.config.fields,
          () => membrane
        );
        logger.info("Repaired a missing integration task binding", {
          integration: candidate.config.integrationKey,
          workspaceId: workspace.workspaceId,
          collection: candidate.dataCollectionId.data_collection_key,
          tableId,
        });
      } catch (error) {
        logger.error("Failed to repair an integration task binding", {
          workspaceId: workspace.workspaceId,
          connectionId: candidate.dataCollectionId.connection_id,
          collection: candidate.dataCollectionId.data_collection_key,
          tableId,
          error,
        });
      } finally {
        pending.delete(key);
      }
    }
  };

  const acquireWorkspace = async (
    workspaceInfo: Workspace,
    organizationId: string
  ) => {
    const workspaceId = workspaceInfo.id;
    acquiring.add(workspaceId);
    const membraneDataPromise = controller.integrationApp.get(workspaceId);
    let membraneData: ReleasableRef<IntegrationAppWorkspaceConfiguration | null>;
    try {
      membraneData = await membraneDataPromise;
    } catch (error) {
      logger.error("Failed to watch integration data for task binding repair", {
        workspaceId,
        error,
      });
      return;
    } finally {
      acquiring.delete(workspaceId);
    }

    if (released || !wantedWorkspaces.value.has(workspaceId)) {
      releaseBestEffort(membraneData);
      return;
    }

    const view = integrations.forOrganization(ref(organizationId));
    const workspace: WorkspaceDataItem = {
      workspaceId,
      workspaceName: workspaceInfo.name,
      membraneData,
    };
    const stop = watch(
      [
        membraneData,
        bindings,
        workspaceDatabases,
        view.connections,
        view.services,
      ],
      () => {
        void repairWorkspace(
          workspace,
          view,
          integrations.client(organizationId)
        );
      },
      { immediate: true }
    );
    owned.set(workspaceId, { workspace, stop });
  };

  const wantedWorkspaces = computed(
    () =>
      new Map(
        profile.value.organizations.flatMap((organization) =>
          organization.workspaces
            .filter((workspace) => workspace.permissions === "write")
            .map(
              (workspace) =>
                [
                  workspace.id,
                  { workspace, organizationId: organization.id },
                ] as const
            )
        )
      )
  );

  const stopMembership = watch(
    () => [...wantedWorkspaces.value.keys()].sort().join("\n"),
    () => {
      for (const workspaceId of [...owned.keys()]) {
        if (!wantedWorkspaces.value.has(workspaceId)) {
          releaseWorkspace(workspaceId);
        }
      }
      for (const [workspaceId, item] of wantedWorkspaces.value) {
        if (!owned.has(workspaceId) && !acquiring.has(workspaceId)) {
          void acquireWorkspace(item.workspace, item.organizationId);
        }
      }
    },
    { immediate: true }
  );

  const release = () => {
    if (released) return;
    released = true;
    stopMembership();
    for (const workspaceId of [...owned.keys()]) releaseWorkspace(workspaceId);
    releaseBestEffort(bindings);
  };

  return { release };
}
